import React, { useEffect, useRef } from "react"
import { ArrowLeft, X } from "lucide-react"
import "./CreatePaperDrawer.style.scss"

interface CreatePaperDrawerProps {
  open: boolean
  busy?: boolean
  onClose: () => void
  children: React.ReactNode
}

/**
 * In-page right panel, same layout as QuestionDetailPanel.
 * Stays inside the learner layout so theme tokens apply.
 */
const CreatePaperDrawer = ({
  open,
  busy = false,
  onClose,
  children,
}: CreatePaperDrawerProps) => {
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    panelRef.current?.focus()
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, busy, onClose])

  if (!open) return null

  return (
    <aside
      ref={panelRef}
      className="create-paper-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-paper-drawer-title"
      tabIndex={-1}
    >
      <header className="create-paper-drawer__header">
        <div className="create-paper-drawer__header-left">
          <button
            type="button"
            className="create-paper-drawer__back"
            aria-label="Back to list"
            disabled={busy}
            onClick={onClose}
          >
            <ArrowLeft size={20} />
          </button>
          <h2 id="create-paper-drawer-title">Create practice paper</h2>
        </div>
        <button
          type="button"
          className="create-paper-drawer__close"
          aria-label="Close"
          disabled={busy}
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </header>
      <div className="create-paper-drawer__body">{children}</div>
    </aside>
  )
}

export default CreatePaperDrawer
